import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OryResponseReader, OryFlowReader, isOryFlowWithUI, type OryResponse } from '@shopware/iam-ory';
import { DomAccessor } from '../browser';

/**
 * Internal type for navigation resolution result.
 * @internal
 */
type NavigationResult =
  | { type: 'spa'; destination: string }
  | { type: 'full-redirect'; url: string };

/**
 * Centralized redirect classification and navigation for Ory flows.
 *
 * @remarks
 * Single source of truth for all redirect handling in the application.
 * Ensures consistent behavior for SPA navigation vs full-page redirects.
 *
 * **Two navigation methods for different use cases:**
 *
 * | Method | Use Case | Called By |
 * |--------|----------|-----------|
 * | `navigate()` | Post-flow navigation with destination resolution | Components |
 * | `performRedirect()` | Forced redirect from known URL | AuthService |
 *
 * **When to use `navigate()`:**
 * - After successful flow execution in components
 * - When you have an Ory response and need to determine where to go next
 * - Resolves destination from: continue_with → anchor nodes → return_to → default
 *
 * **When to use `performRedirect()`:**
 * - When Ory API says "redirect to this URL" (redirect_browser_to, OryBrowserRedirectError)
 * - When you have a specific URL and need to execute the redirect
 * - Used internally by AuthService for forced redirects
 *
 * @see navigate - For component-level post-flow navigation
 * @see performRedirect - For infrastructure-level forced redirects
 */
@Injectable({
  providedIn: 'root',
})
export class OryNavigationResolver {
  readonly #domAccessor = inject(DomAccessor);
  readonly #router = inject(Router);

  /**
   * Classifies redirect URL for SPA navigation vs full-page redirect.
   *
   * @remarks
   * **Classification rules (priority order):**
   * 1. `/oauth2/*` → full redirect (Ory Hydra endpoints)
   * 2. `/self-service/X?flow=...` → full redirect (completing flow via Ory API)
   * 3. `/self-service/X/flows?id=...` → SPA to `/X?flow=...` (flow replacement)
   * 4. `/self-service/X/browser` → SPA to `/X` (flow creation)
   * 5. Same origin with `?flow=` → SPA navigation
   * 6. External URLs → full redirect
   *
   * Transforms Ory API format (`?id=`) to app routing format (`?flow=`).
   *
   * @example
   * ```typescript
   * classifyRedirect('/oauth2/auth?client_id=...')
   * // → { type: 'full-redirect', spaRoute: '' }
   *
   * classifyRedirect('/self-service/verification/flows?id=abc')
   * // → { type: 'spa-navigation', spaRoute: '/verification?flow=abc' }
   *
   * classifyRedirect('https://github.com/login/oauth')
   * // → { type: 'full-redirect', spaRoute: '' }
   * ```
   */
  classifyRedirect(url: string): {
    type: 'full-redirect' | 'spa-navigation';
    spaRoute: string;
  } {
    const currentOrigin = this.#domAccessor.window.location.origin;

    try {
      const targetUrl = new URL(url, currentOrigin);

      // 0. OAuth2/Hydra API endpoints - ALWAYS full redirect
      // These are Ory Hydra API endpoints that must trigger browser redirects
      // to continue the OAuth2 authorization flow (even on same origin)
      // Patterns:
      // - /oauth2/auth - Authorization endpoint
      // - /oauth2/sessions - Session management
      // - /oauth2/token - Token endpoint (typically server-side, but include for completeness)
      if (targetUrl.pathname.startsWith('/oauth2/')) {
        return { type: 'full-redirect', spaRoute: '' };
      }

      // 1. Self-service URLs (MUST check first before origin check)
      // These need transformation regardless of origin (same-origin or cross-origin)
      if (targetUrl.pathname.startsWith('/self-service/')) {
        const hasFlowParam = targetUrl.searchParams.has('flow');
        const hasIdParam = targetUrl.searchParams.has('id');

        if (hasFlowParam) {
          // WITH flow param = completing pending flow (e.g., after reauthentication)
          // Must do full redirect to Ory API to complete the flow
          return { type: 'full-redirect', spaRoute: '' };
        }

        // Extract flow type from path
        const match = targetUrl.pathname.match(/\/self-service\/([^/]+)/);
        const flowType = match ? match[1] : null;

        if (!flowType) {
          return { type: 'spa-navigation', spaRoute: '/' };
        }

        // WITHOUT flow param but WITH id param = fetching existing flow
        // Transform to SPA route: /self-service/verification/flows?id=123 → /verification?flow=123
        if (hasIdParam) {
          const flowId = targetUrl.searchParams.get('id');
          return {
            type: 'spa-navigation',
            spaRoute: `/${flowType}?flow=${flowId}`
          };
        }

        // WITHOUT any params = creating new flow
        // Transform to SPA route: /self-service/login/browser → /login
        return {
          type: 'spa-navigation',
          spaRoute: `/${flowType}${targetUrl.search}`
        };
      }

      // 2. Same origin (relative or same domain) → SPA navigation
      if (targetUrl.origin === currentOrigin) {
        return {
          type: 'spa-navigation',
          spaRoute: targetUrl.pathname + targetUrl.search,
        };
      }

      // 3. Flow URLs (from show_*_ui actions)
      // These have ?flow= but are NOT /self-service/
      // Examples: http://auth-api.shopware.com/verification?flow=123
      //           http://localhost:5100/recovery?flow=456
      // Extract path for SPA navigation (ignore domain/port differences)
      if (targetUrl.searchParams.has('flow')) {
        return {
          type: 'spa-navigation',
          spaRoute: targetUrl.pathname + targetUrl.search,
        };
      }

      // 4. Everything else → full redirect (external OAuth, consent, etc.)
      return { type: 'full-redirect', spaRoute: '' };
    } catch (error) {
      // Malformed URL - log error and treat as full redirect for safety
      console.error('Failed to parse redirect URL:', url, error);
      return { type: 'full-redirect', spaRoute: '' };
    }
  }

  /**
   * Executes redirect to a known URL.
   *
   * @remarks
   * **Use this when:** Ory API provides a specific redirect URL (forced redirect).
   * **Use `navigate()` when:** You need to determine where to go based on response context.
   *
   * Returns the redirect type so callers can handle Observable semantics:
   * - `full-redirect`: Page unloading, return `NEVER`
   * - `spa-navigation`: Angular route change, return `EMPTY` or continue
   *
   * @returns The type of redirect performed
   *
   * @example
   * ```typescript
   * // In AuthService - handling forced redirect from Ory
   * if (error instanceof OryBrowserRedirectError) {
   *   const type = this.#navigationResolver.performRedirect(error.redirectTo);
   *   return type === 'full-redirect' ? NEVER : EMPTY;
   * }
   * ```
   */
  performRedirect(url: string): 'full-redirect' | 'spa-navigation' {
    const { type, spaRoute } = this.classifyRedirect(url);

    if (type === 'full-redirect') {
      this.#domAccessor.window.location.href = url;
      return 'full-redirect';
    }

    void this.#router.navigateByUrl(spaRoute);
    return 'spa-navigation';
  }

  /**
   * Determines navigation destination from Ory response.
   *
   * @internal
   * Priority-based resolution:
   * 1. continue_with actions (show_verification_ui, redirect_browser_to, etc.)
   * 2. Continue anchor nodes (passed_challenge flows with OAuth2 context)
   * 3. flow.return_to (user's original destination)
   * 4. defaultDestination (component fallback)
   */
  #getDestination(
    response: OryResponse,
    flow?: { return_to?: string },
    defaultDestination = '/'
  ): NavigationResult {
    // Priority 1: Check continue_with using infrastructure helper
    // OryResponseReader prioritizes show_*_ui actions over redirect_browser_to
    const oryRedirect = OryResponseReader.redirectUrl(response);

    if (oryRedirect) {
      const { type, spaRoute } = this.classifyRedirect(oryRedirect);

      if (type === 'spa-navigation') {
        return { type: 'spa', destination: spaRoute };
      }

      return { type: 'full-redirect', url: oryRedirect };
    }

    // Priority 2: Check for continue anchor node in flow UI
    // This handles passed_challenge flows (e.g., verification with OAuth2 context)
    // where the redirect URL is in an anchor node, not continue_with
    if (response && isOryFlowWithUI(response)) {
      const continueUrl = OryFlowReader.getContinueUrl(response);
      if (continueUrl) {
        const { type, spaRoute } = this.classifyRedirect(continueUrl);

        if (type === 'spa-navigation') {
          return { type: 'spa', destination: spaRoute };
        }

        return { type: 'full-redirect', url: continueUrl };
      }
    }

    // Priority 3: User's original destination (return_to)
    // Must classify - return_to may be an OAuth2 URL requiring full-redirect
    if (flow?.return_to) {
      const { type, spaRoute } = this.classifyRedirect(flow.return_to);

      if (type === 'spa-navigation') {
        return { type: 'spa', destination: spaRoute };
      }

      return { type: 'full-redirect', url: flow.return_to };
    }

    // Priority 4: Component's default
    return { type: 'spa', destination: defaultDestination };
  }

  /**
   * Navigates after successful flow execution in components.
   *
   * @remarks
   * **Use this when:** Component completes a flow and needs to navigate based on context.
   * **Use `performRedirect()` when:** You have a specific URL from Ory to redirect to.
   *
   * **Destination resolution priority:**
   * 1. `continue_with` actions (show_verification_ui, redirect_browser_to)
   * 2. Continue anchor nodes (passed_challenge flows with OAuth2 context)
   * 3. `flow.return_to` (user's original destination)
   * 4. `defaultDestination` parameter
   *
   * Handles both SPA navigation and full-page redirects (OAuth2, external URLs).
   *
   * @param response - Ory API response from executeFlow()
   * @param flow - Current flow object (for return_to context)
   * @param defaultDestination - Fallback route if no redirect found
   * @param options - Angular router options (e.g., replaceUrl)
   *
   * @example
   * ```typescript
   * // After successful verification
   * this.#verificationService.verifyCode(this.flow(), code)
   *   .subscribe({
   *     next: (response) => {
   *       this.#notificationService.notifySuccess(...);
   *       this.#navigationResolver.navigate(
   *         response,
   *         this.flow(),
   *         '/settings',
   *         { replaceUrl: true }
   *       );
   *     }
   *   });
   * ```
   */
  navigate(
    response: OryResponse,
    flow?: { return_to?: string },
    defaultDestination = '/',
    options?: { replaceUrl?: boolean }
  ): void {
    const result = this.#getDestination(response, flow, defaultDestination);

    if (result.type === 'spa') {
      void this.#router.navigateByUrl(result.destination, options);
    } else {
      // Full-page redirect - delegate to performRedirect for consistency
      this.performRedirect(result.url);
    }
  }
}

